/**
 * Complaint Service
 * 
 * Handles all complaint-related API calls
 */

import apiClient, { API_ENDPOINTS } from './api';
import { handleApiError } from '../utils/apiErrorHandler';

const complaintService = {
  /**
   * Get list of complaints
   * @param {Object} params - Query parameters (page, limit, status, etc.)
   * @returns {Promise<Object>} List of complaints
   */
  async getComplaints(params = {}) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.complaints.list, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch complaints' });
    }
  },

  /**
   * Get list of CVO complaints
   * @param {Object} params - Query parameters (page, limit, status, etc.)
   * @returns {Promise<Object>} List of CVO complaints
   */
  async getCVOComplaints(params = {}) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.complaints.cvoList, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch CVO complaints' });
    }
  },

  /**
   * Get authority assigned complaints
   * @param {Object} params - Optional query parameters
   * @returns {Promise<Object>} Assigned complaints for authority
   */
  async getAuthorityAssignedComplaints(params = {}) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.authority.assignedComplaints, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch assigned complaints' });
    }
  },

  /**
   * Get authority offline complaints
   * @param {Object} params - Optional query parameters
   * @returns {Promise<Object>} Offline complaints for authority
   */
  async getAuthorityOfflineComplaints(params = {}) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.authority.listOffline, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch offline complaints' });
    }
  },

  /**
   * Get CVO complaint by ID
   * @param {string} id - Complaint ID
   * @returns {Promise<Object>} CVO complaint details
   */
  async getCVOComplaint(id) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.complaints.cvoDetail(id));
      console.log('response', response);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch CVO complaint details' });
    }
  },

  /**
   * Get complaint by ID
   * @param {string} id - Complaint ID
   * @returns {Promise<Object>} Complaint details
   */
  async getComplaint(id) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.complaints.detail(id));
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch complaint details' });
    }
  },

  /**
   * Create new complaint
   * @param {Object} complaintData - Complaint data
   * @returns {Promise<Object>} Created complaint
   */
  async createComplaint(complaintData) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.complaints.create, complaintData);
      return {
        success: true,
        data: response.data,
        message: 'Complaint created successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to create complaint' });
    }
  },

  /**
   * Update complaint
   * @param {string} id - Complaint ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Updated complaint
   */
  async updateComplaint(id, updateData) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.complaints.update(id), updateData);
      return {
        success: true,
        data: response.data,
        message: 'Complaint updated successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to update complaint' });
    }
  },

  /**
   * Delete complaint
   * @param {string} id - Complaint ID
   * @returns {Promise<Object>}
   */
  async deleteComplaint(id) {
    try {
      await apiClient.delete(API_ENDPOINTS.complaints.delete(id));
      return {
        success: true,
        message: 'Complaint deleted successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to delete complaint' });
    }
  },

  /**
   * Get complaint status by complaint number
   * @param {Object} data - { complaintNumber, email/phone, otp }
   * @returns {Promise<Object>} Complaint status
   */
  async getComplaintStatus(data) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.complaints.statusByNumber, data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch complaint status' });
    }
  },

  /**
   * Get complaint status updates/history
   * @param {string} id - Complaint ID
   * @returns {Promise<Object>} Status updates
   */
  async getStatusUpdates(id) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.complaints.statusUpdates(id));
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch status updates' });
    }
  },

  /**
   * Get complaint history/timeline
   * @param {string} id - Complaint ID
   * @returns {Promise<Object>} Complaint history
   */
  async getComplaintHistory(id) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.complaints.history(id));
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch complaint history' });
    }
  },

  /**
   * Get complaint logs
   * @param {string|number} id - Complaint ID
   * @returns {Promise<Object>} Complaint logs
   */
  async getComplaintLogs(id) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.complaints.logs(id));
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch complaint logs' });
    }
  },

  /**
   * Upload document for complaint
   * @param {string} id - Complaint ID
   * @param {FormData} formData - Form data with files
   * @returns {Promise<Object>}
   */
  async uploadDocument(id, formData) {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.complaints.uploadDocument(id),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return {
        success: true,
        data: response.data,
        message: 'Document uploaded successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to upload document' });
    }
  },

  /**
   * Delete document from complaint
   * @param {string} complaintId - Complaint ID
   * @param {string} documentId - Document ID
   * @returns {Promise<Object>}
   */
  async deleteDocument(complaintId, documentId) {
    try {
      await apiClient.delete(API_ENDPOINTS.complaints.deleteDocument(complaintId, documentId));
      return {
        success: true,
        message: 'Document deleted successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to delete document' });
    }
  },

  /**
   * Get complaint documents
   * @param {string} id - Complaint ID
   * @returns {Promise<Object>} List of documents
   */
  async getDocuments(id) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.complaints.documents(id));
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch documents' });
    }
  },

  /**
   * Update CVO complaint status
   * @param {string} id - Complaint ID
   * @param {Object} statusData - Status data { status: string }
   * @returns {Promise<Object>} Updated complaint status
   */
  async updateCVOComplaintStatus(id, statusData) {
    try {
      const response = await apiClient.patch(API_ENDPOINTS.complaints.cvoStatus(id), statusData);
      return {
        success: true,
        data: response.data,
        message: 'Complaint status updated successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to update complaint status' });
    }
  },

  /**
   * Update CVO complaint data (assign_complain_type, department_name, assigned_day_by_cvo, priority, remark)
   * @param {string} id - Complaint ID
   * @param {Object} updateData - Update data { assign_complain_type, department_name, assigned_day_by_cvo, priority, remark }
   * @returns {Promise<Object>} Updated complaint
   */
  async updateCVOComplaintData(id, updateData) {
    try {
      const response = await apiClient.patch(API_ENDPOINTS.complaints.cvoUpdate(id), updateData);
      return {
        success: true,
        data: response.data,
        message: 'Complaint updated successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to update complaint data' });
    }
  },

  /**
   * Update authority complaint (complaint_type, template_type, remark)
   * @param {string|number} id - Complaint ID
   * @param {Object} updateData - Update data { complaint_type, template_type, remark }
   * @returns {Promise<Object>} Updated complaint
   */
  async updateAuthorityComplaint(id, updateData) {
    try {
      const response = await apiClient.patch(API_ENDPOINTS.authority.updateComplaint(id), updateData);
      return {
        success: true,
        data: response.data,
        message: 'Complaint updated successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to update complaint' });
    }
  },

  /**
   * Get user online complaints list
   * @param {Object} params - Optional query parameters
   * @returns {Promise<Object>} List of user complaints
   */
  async getUserOnlineComplaints(params = {}) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.user.listOnline, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch user complaints' });
    }
  },

  /**
   * Create offline complaint (Authority only)
   * @param {Object} complaintData - Offline complaint data
   * @returns {Promise<Object>} Created complaint
   */
  async createOfflineComplaint(complaintData) {
    try {
      // Create FormData for file upload
      const formDataObj = new FormData();
      
      // Add complaint type
      formDataObj.append('complaint_type', 'offline');
      
      // Add complaint details
      formDataObj.append('your_complaint', complaintData.complaint || '');
      
      // Add address
      if (complaintData.address) {
        formDataObj.append('address', complaintData.address);
      }
      
      // Add complainant information
      if (complaintData.name) {
        formDataObj.append('name', complaintData.name);
      }
      if (complaintData.email) {
        formDataObj.append('email', complaintData.email);
      }
      if (complaintData.mobile) {
        formDataObj.append('mobile', complaintData.mobile);
      }
      
      // Add supporting documents
      if (complaintData.supportingDocuments && complaintData.supportingDocuments.length > 0) {
        complaintData.supportingDocuments.forEach((file) => {
          formDataObj.append('supporting_documents[]', file);
        });
      }
      
      // Add general documents
      if (complaintData.generalDocuments && complaintData.generalDocuments.length > 0) {
        complaintData.generalDocuments.forEach((file) => {
          formDataObj.append('general_documents[]', file);
        });
      }
      
      const response = await apiClient.post(
        API_ENDPOINTS.authority.createOffline,
        formDataObj,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      return {
        success: true,
        data: response.data,
        message: 'Offline complaint created successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to create offline complaint' });
    }
  },

  /**
   * Check complaint status (for status tracking entry)
   * @param {Object} data - { complaint_id, identifier, captcha_key, captcha }
   * @returns {Promise<Object>} Status check response
   */
  async checkComplaintStatus(data) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.user.statusCheck, data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to check complaint status' });
    }
  },

  /**
   * Verify OTP for status check
   * @param {Object} data - { identifier, otp, captcha, captcha_key }
   * @returns {Promise<Object>} Verification response
   */
  async verifyStatusCheck(data) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.user.verifyCheck, data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to verify OTP' });
    }
  },

  /**
   * Get complaint history
   * @param {string} complaintId - Complaint ID
   * @param {string} identifier - Email or phone number (optional for authenticated users)
   * @returns {Promise<Object>} Complaint history
   */
  async getUserComplaintHistory(complaintId, identifier = null) {
    try {
      const params = { 
        complaint_id: complaintId
      };
      
      // Only add identifier if provided
      if (identifier) {
        params.identifier = identifier;
      }
      
      const response = await apiClient.get(API_ENDPOINTS.user.complaintHistory, {
        params,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch complaint history' });
    }
  },

  /**
   * Get complaint history logs (for authenticated users in dashboard)
   * @param {string} complaintId - Complaint ID
   * @returns {Promise<Object>} Complaint history logs
   */
  async getUserComplaintHistoryLogs(complaintId) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.user.complaintHistoryLogs, {
        params: { 
          complaint_id: complaintId
        },
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch complaint history logs' });
    }
  },
};

export default complaintService;

